import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useCart } from '../context/CartContext';
import { ordersAPI, paymentsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { formatPrice } from '../lib/utils';
import { toast } from 'sonner';
import { ShoppingBag } from 'lucide-react';

export default function Checkout() {
  const { cart, getTotal, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const subtotal = getTotal();
  const vat = subtotal * 0.24;
  const total = subtotal + vat;

  const handleCheckout = async () => {
    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    setLoading(true);

    try {
      // Create order
      const orderResponse = await ordersAPI.create({ items: cart });
      const order = orderResponse.data;

      // Create Stripe checkout session
      const origin = window.location.origin;
      const checkoutResponse = await paymentsAPI.createCheckout({
        order_id: order.id,
        origin_url: origin,
      });

      // Redirect to Stripe - open in new tab to avoid iframe restrictions
      window.open(checkoutResponse.data.url, '_blank');
      
      // Clear cart after redirecting
      clearCart();
      toast.success('Redirecting to payment page...');
      setLoading(false);
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Checkout failed');
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    return (
      <Layout>
        <Card>
          <CardContent className="pt-6 text-center py-12">
            <ShoppingBag className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Your cart is empty</h3>
            <p className="text-muted-foreground mb-4">Add some services to proceed</p>
            <Button onClick={() => navigate('/services')}>Browse Services</Button>
          </CardContent>
        </Card>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-heading font-bold mb-2">Checkout</h1>
          <p className="text-muted-foreground">Review your order and proceed to payment</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Order Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              {cart.map((item, idx) => (
                <div key={idx} className="flex justify-between items-start pb-3 border-b" data-testid={`checkout-item-${idx}`}>
                  <div>
                    <div className="font-semibold">{item.service_name}</div>
                    <div className="text-sm text-muted-foreground">{item.tier_name}</div>
                  </div>
                  <div className="font-semibold">{formatPrice(item.price)}</div>
                </div>
              ))}
            </div>

            <div className="space-y-2 pt-4">
              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span className="font-semibold">{formatPrice(subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>VAT (24%):</span>
                <span>{formatPrice(vat)}</span>
              </div>
              <div className="border-t pt-2 flex justify-between text-xl font-bold">
                <span>Total:</span>
                <span className="text-primary">{formatPrice(total)}</span>
              </div>
            </div>

            <div className="pt-4">
              <Button
                className="w-full"
                size="lg"
                onClick={handleCheckout}
                disabled={loading}
                data-testid="complete-checkout-button"
              >
                {loading ? 'Processing...' : `Pay ${formatPrice(total)}`}
              </Button>
              <p className="text-xs text-center text-muted-foreground mt-4">
                You will be redirected to Stripe for secure payment
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
